import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Sparkles, CheckCircle2, Circle, ChevronRight, ExternalLink, Loader2,
  Rocket, Trophy, Gift, ArrowRight, Zap, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  seo_providers: '🔍',
  ads: '🎯',
  crm: '💼',
  analytics: '📊',
};

export default function Setup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/setup/status`, { withCredentials: true });
      setData(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Accès admin requis');
        navigate('/');
      } else {
        toast.error(e.response?.data?.detail || 'Erreur');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
        </div>
      </Layout>
    );
  }

  if (!data) return <Layout><div className="p-6" /></Layout>;

  const progress = data.overall_progress;
  const isComplete = progress === 100;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="setup-page">
        {/* Hero progress banner */}
        <div className={`rounded-xl p-6 border-2 ${
          isComplete
            ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300'
            : 'bg-gradient-to-br from-[#002FA7]/5 to-indigo-50 border-[#002FA7]/20'
        }`} data-testid="setup-hero">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                isComplete ? 'bg-emerald-500' : 'bg-[#002FA7]'
              }`}>
                {isComplete ? <Trophy className="w-7 h-7 text-white" /> : <Rocket className="w-7 h-7 text-white" />}
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-neutral-900">
                  {isComplete ? 'Configuration terminée 🎉' : 'Configuration de la plateforme'}
                </h1>
                <p className="text-neutral-600 mt-1 max-w-2xl">
                  {isComplete
                    ? 'Toutes les intégrations sont prêtes. Bonne chasse aux leads !'
                    : 'Suivez ces étapes pour activer le plein potentiel de la plateforme. Chaque outil externe est accompagné du lien d\'affiliation personnel quand il est configuré.'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-[#002FA7]" data-testid="setup-progress-percent">
                {progress}%
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {data.items_done}/{data.items_total} intégrations
              </div>
              {!data.required_ready && (
                <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1 bg-amber-100 px-2 py-0.5 rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  Requis : {data.required_progress}
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 h-2.5 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isComplete ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#002FA7] to-indigo-500'
              }`}
              style={{ width: `${progress}%` }}
              data-testid="setup-progress-bar"
            />
          </div>
        </div>

        {/* Categories */}
        {data.categories.map(cat => (
          <CategorySection key={cat.id} category={cat} />
        ))}
      </div>
    </Layout>
  );
}

function CategorySection({ category }) {
  const doneCount = category.items.filter(i => i.configured).length;
  const totalCount = category.items.length;
  const complete = category.any_of ? doneCount >= 1 : doneCount === totalCount;

  return (
    <section
      className="bg-white border border-neutral-200 rounded-lg"
      data-testid={`setup-category-${category.id}`}
    >
      <header className="p-5 border-b border-neutral-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center text-xl flex-shrink-0">
          {CATEGORY_ICONS[category.id] || '🔗'}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-neutral-900 flex items-center gap-2 flex-wrap">
            {category.label}
            {category.required && (
              <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 rounded-full font-medium uppercase">
                Requis
              </span>
            )}
            {category.any_of && (
              <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded-full font-medium">
                1 au choix
              </span>
            )}
          </h2>
          <p className="text-sm text-neutral-600 mt-0.5">{category.description}</p>
        </div>
        <div className={`text-sm font-semibold ${complete ? 'text-emerald-600' : 'text-neutral-500'}`}>
          {doneCount}/{totalCount}
        </div>
      </header>

      <div className="divide-y divide-neutral-100">
        {category.items.map(item => (
          <SetupItem key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function SetupItem({ item }) {
  const [expanded, setExpanded] = useState(!item.configured);
  const configured = item.configured;
  const inProgress = item.in_progress;

  // Use affiliate URL if available, else default
  const signupUrl = item.affiliate_url || item.default_url;
  const hasAffiliate = !!item.affiliate_url;

  return (
    <div className="p-5" data-testid={`setup-item-${item.id}`}>
      <div
        className="flex items-start gap-3 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-shrink-0 mt-0.5">
          {configured ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" data-testid={`setup-item-check-${item.id}`} />
          ) : inProgress ? (
            <div className="w-5 h-5 rounded-full border-2 border-amber-400 bg-amber-100 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            </div>
          ) : (
            <Circle className="w-5 h-5 text-neutral-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-neutral-900">{item.name}</h3>
            {configured && (
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium uppercase">
                Configuré
              </span>
            )}
            {inProgress && !configured && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium uppercase">
                En cours
              </span>
            )}
            {hasAffiliate && !configured && (
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full font-medium uppercase inline-flex items-center gap-0.5">
                <Gift className="w-2.5 h-2.5" /> Lien partenaire
              </span>
            )}
          </div>
          {item.internal_hint && (
            <p className="text-xs text-neutral-500 mt-0.5">{item.internal_hint}</p>
          )}
        </div>
        <ChevronRight className={`w-4 h-4 text-neutral-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>

      {expanded && (
        <div className="mt-4 ml-8 space-y-3">
          {/* Checklist steps */}
          {item.steps && item.steps.length > 0 && (
            <ol className="space-y-1.5" data-testid={`setup-steps-${item.id}`}>
              {item.steps.map((step, i) => (
                <li key={`${item.id}-step-${i}-${step.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-neutral-700">
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold mt-0.5 ${
                    configured ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-600'
                  }`}>
                    {configured ? '✓' : i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {/* Signup link (affiliate if configured) */}
            {signupUrl && !configured && (
              <a
                href={signupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 transition-opacity"
                data-testid={`setup-signup-${item.id}`}
              >
                {hasAffiliate ? <Sparkles className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                {hasAffiliate ? 'S\'inscrire (mon lien d\'affiliation)' : 'Visiter le site'}
              </a>
            )}

            {/* Internal link to config page */}
            {item.internal_link && (
              <Link
                to={item.internal_link}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] transition-colors"
                data-testid={`setup-configure-${item.id}`}
              >
                <Zap className="w-3.5 h-3.5" />
                {configured ? 'Gérer' : 'Configurer'} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}

            {/* Commission note */}
            {hasAffiliate && item.commission_note && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium bg-emerald-50 px-2 py-1 rounded">
                💰 {item.commission_note}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
